import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import ResourcePicker from '../components/ResourcePicker';
import MetricInput from '../components/MetricInput';

import ControlledDraftJson, { resourceEditContent } from '../components/ControlledDraftJson';
import ProcedureAuthoringMetadata from '../components/ProcedureAuthoringMetadata';
import ProcedureRevisionBar from '../components/ProcedureRevisionBar';

const API_BASE = process.env.REACT_APP_API_URL;
const JIG_TYPES = ['Assembly Fixture','Test Fixture','Calibration Jig','Inspection Jig','Programming Jig','Soldering Fixture','Packaging Jig','Generic'];
const SUPPLIERS = ['gpv','ubiqconn','acmepoint','hk_ringer'];
const STATUS_COLORS = { Draft:'#94a3b8','In Review':'#60a5fa',Released:'#46BB89',Superseded:'#f59e0b',Obsolete:'#EC2124' };
const TRANSITIONS = { submit:{from:'Draft',to:'In Review',label:'Submit for Review'},revise:{from:'Released',to:'Draft',label:'Revise / New Draft'},approve:{from:'In Review',to:'Released',label:'Approve & Release'},reject:{from:'In Review',to:'Draft',label:'Reject'},obsolete:{from:null,to:'Obsolete',label:'Mark Obsolete'} };

function normalizeRole(r) {
  if (!r) return ''; if (Array.isArray(r)) return (r[0]||'').toLowerCase().trim();
  return String(r).toLowerCase().trim();
}
function StatusBadge({ status }) {
  const c = STATUS_COLORS[status]||'#666';
  return <span style={{ display:'inline-block',padding:'2px 10px',borderRadius:20,fontSize:11,
    fontWeight:700,letterSpacing:'0.05em',textTransform:'uppercase',background:c+'22',color:c }}>{status}</span>;
}

export default function JigDetail() {
  const { id } = useParams(); const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedRevision = searchParams.get('revision') || '';
  const [availableRevisions, setAvailableRevisions] = useState([]);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [jsonBusy, setJsonBusy] = useState(false);
  const loadSequence = useRef(0);
  const { getToken } = useAuth(); const { user } = useUser();
  const role = normalizeRole(user?.publicMetadata?.role) || normalizeRole(user?.publicMetadata?.roles);
  const canManage = ['admin','quality-manager','production-engineer','service'].includes(role);
  const supplierKey = ['gpv','ubiqconn','acmepoint','hk_ringer'].includes(String(role||'').replace(/^test-/,''));

  const [jig, setJig] = useState(null);
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [usedInFlows, setUsedInFlows] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [changeSummary, setChangeSummary] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [actionModal, setActionModal] = useState(null);
  const [actionNote, setActionNote] = useState('');
  const [error, setError] = useState('');
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verForm, setVerForm] = useState({ date:'', method:'', notes:'', passed:true, certificate:'',
    verifiedByOrg:'', referenceStandardUsed:'', traceableTo:'',
    asFound:'', asLeft:'', outOfTolerance:false, impactAssessment:'', customerNotified:false });
  const [verSaving, setVerSaving] = useState(false);
  const [showMaintModal, setShowMaintModal] = useState(false);
  const [maintForm, setMaintForm] = useState({ date:'', type:'Preventive', workDone:'', notes:'' });
  const [maintSaving, setMaintSaving] = useState(false);

  const load = useCallback(async () => {
    const sequence = ++loadSequence.current;
    setLoading(true); setError(''); setEditing(false);
    try {
      const token = await getToken();
      const query = requestedRevision ? `?revision=${encodeURIComponent(requestedRevision)}` : '';
      const res = await window.fetch(`${API_BASE}/quality/jigs/${id}${query}`, { headers:{ Authorization:`Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.message || data.error || 'Could not load document');
      if (sequence === loadSequence.current) {
        setJig(data.jig); setForm(JSON.parse(JSON.stringify(data.jig)));
        setAvailableRevisions(data.availableRevisions || []); setSnapshot(data.snapshot || null);
      }
    } catch (err) { if (sequence === loadSequence.current) { setError(err.message); setJig(null); } }
    finally { if (sequence === loadSequence.current) setLoading(false); }
  }, [id, getToken, requestedRevision]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!id) return;
    getToken().then(token =>
      window.fetch(`${API_BASE}/quality/process-flows/used-in?resource=jig&id=${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.json()).then(d => { if (d.ok) setUsedInFlows(d.flows || []); }).catch(() => {})
    );
  }, [id, getToken]);

  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const addTag = t => { const v=t.trim(); if(!v||(form.applicableProducts||[]).includes(v)) return; set('applicableProducts',[...(form.applicableProducts||[]),v]); setTagInput(''); };
  const removeTag = t => set('applicableProducts',(form.applicableProducts||[]).filter(x=>x!==t));
  const setDim = (k,v) => setForm(f=>({...f,dimensions:{...f.dimensions,[k]:v}}));
  const toggleSupplier = s => {
    const cur = form.visibility?.suppliers||[];
    const next = cur.includes(s)?cur.filter(x=>x!==s):[...cur,s];
    setForm(f=>({...f,visibility:{...f.visibility,suppliers:next,supplierAccess:next.length?'Specific':'None'}}));
  };

  const handleSave = async () => {
    if (requestedRevision || snapshot || jig?.currentStatus !== 'Draft') return;
    setSaving(true); setError('');
    try {
      const token = await getToken();
      const res = await window.fetch(`${API_BASE}/quality/jigs/${id}`, {
        method:'PUT', headers:{ Authorization:`Bearer ${token}`,'Content-Type':'application/json' },
        body: JSON.stringify({ ...resourceEditContent(form), changeSummary, expectedVersion:jig.__v }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.message||data.error||'Save failed');
      setJig(data.jig); setForm(JSON.parse(JSON.stringify(data.jig)));
      setEditing(false); setShowSaveModal(false); setChangeSummary('');
    } catch(err) { setError(err.message); } finally { setSaving(false); }
  };

  const handleAction = async action => {
    if (requestedRevision || snapshot || jsonBusy) return;
    setSaving(true); setError('');
    try {
      const token = await getToken();
      const res = await window.fetch(`${API_BASE}/quality/jigs/${id}/submit`, {
        method:'POST', headers:{ Authorization:`Bearer ${token}`,'Content-Type':'application/json' },
        body: JSON.stringify({ action, changeSummary: actionNote, expectedVersion:jig.__v }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.message||data.error||'Action failed');
      setJig(data.jig); setForm(JSON.parse(JSON.stringify(data.jig)));
      setActionModal(null); setActionNote('');
    } catch(err) { setError(err.message); } finally { setSaving(false); }
  };

  const availableActions = jig && !requestedRevision && !snapshot ? Object.entries(TRANSITIONS)
    .filter(([,t]) => !t.from || t.from === jig.currentStatus)
    .map(([key,t]) => ({ key, label:t.label }))
    .filter(a => canManage
      || (supplierKey && a.key === 'submit' && jig?.createdBy === user?.id && jig?.currentStatus !== 'Released')
      || (supplierKey && a.key === 'revise' && jig?.currentStatus === 'Released')) : [];
  const canEditThis = !requestedRevision && !snapshot && jig?.currentStatus === 'Draft'
    && (canManage || (supplierKey && (jig?.createdBy === user?.id
      || ((jig?.visibility?.suppliers || []).includes(String(role).replace(/^test-/,''))
        && (jig?.revisions || []).some(r => r.status === 'Released')))));
  const unresolvedBlockers = (jig?.releaseBlockers || []).some(b => !b.resolved);
  const canLogEvents = !requestedRevision && !snapshot && !['Obsolete','Superseded'].includes(jig?.currentStatus)
    && (canManage || (supplierKey && (jig?.createdBy === user?.id || (jig?.visibility?.suppliers || []).includes(String(role).replace(/^test-/,'')))));

  const logVerification = async () => {
    if (requestedRevision || snapshot) return;
    setVerSaving(true);
    try {
      const token = await getToken();
      const res = await window.fetch(`${API_BASE}/quality/jigs/${id}/verify`, {
        method:'POST', headers:{ Authorization:`Bearer ${token}`,'Content-Type':'application/json' },
        body: JSON.stringify({ ...verForm, expectedVersion:jig.__v }),
      });
      const data = await res.json();
      if (data.ok) { setJig(data.jig); setForm(JSON.parse(JSON.stringify(data.jig))); setShowVerifyModal(false);
        setVerForm({ date:'', method:'', notes:'', passed:true, certificate:'', verifiedByOrg:'', referenceStandardUsed:'', traceableTo:'', asFound:'', asLeft:'', outOfTolerance:false, impactAssessment:'', customerNotified:false }); }
    } catch {} finally { setVerSaving(false); }
  };

  const logMaintenance = async () => {
    if (requestedRevision || snapshot) return;
    setMaintSaving(true);
    try {
      const token = await getToken();
      const res = await window.fetch(`${API_BASE}/quality/jigs/${id}/maintenance`, {
        method:'POST', headers:{ Authorization:`Bearer ${token}`,'Content-Type':'application/json' },
        body: JSON.stringify({ ...maintForm, expectedVersion:jig.__v }),
      });
      const data = await res.json();
      if (data.ok) { setJig(data.jig); setForm(JSON.parse(JSON.stringify(data.jig))); setShowMaintModal(false);
        setMaintForm({ date:'', type:'Preventive', workDone:'', notes:'' }); }
    } catch {} finally { setMaintSaving(false); }
  };

  // Derived freshness of the verification schedule (independent of the doc lifecycle).
  const verStatus = (() => {
    if (!jig?.verificationRequired) return null;
    if (!jig.lastVerifiedAt) return 'never';
    if (jig.nextVerificationDue && new Date(jig.nextVerificationDue) < new Date()) return 'overdue';
    if (jig.nextVerificationDue && new Date(jig.nextVerificationDue) < new Date(Date.now() + 30*86400000)) return 'due-soon';
    return 'ok';
  })();
  const verStatusColor = { never:'#94a3b8', overdue:'#EC2124', 'due-soon':'#f59e0b', ok:'#46BB89' };
  const verStatusLabel = { never:'Never Verified', overdue:'Overdue', 'due-soon':'Due Soon', ok:'Up to Date' };

  if (loading) return <div style={{ padding:40,textAlign:'center',color:'var(--ni-text-muted)' }}>Loading…</div>;

  if (!jig) return <div role="alert">{error || 'Document unavailable'} <button onClick={load}>Retry</button></div>;

  const inp = { background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',
    color:'var(--ni-text)',borderRadius:6,padding:'8px 12px',fontSize:13,width:'100%',boxSizing:'border-box' };
  const labelS = { display:'block',fontSize:11,fontWeight:700,letterSpacing:'0.06em',
    textTransform:'uppercase',color:'var(--ni-text-muted)',marginBottom:5 };
  const readVal = (v) => <span style={{ fontSize:14 }}>{v||<span style={{ color:'var(--ni-text-muted)' }}>—</span>}</span>;

  return (
    <div style={{ maxWidth:760 }}>
      {/* Header */}
      <div style={{ display:'flex',alignItems:'center',gap:12,marginBottom:6 }}>
        <button onClick={()=>navigate('/quality/jigs')}
          style={{ background:'none',border:'none',color:'var(--ni-text-muted)',cursor:'pointer',fontSize:20,padding:0 }}>←</button>
        <div>
          <div style={{ fontSize:11,fontFamily:'monospace',color:'#a78bfa',marginBottom:2 }}>{jig.documentNumber}</div>
          <h2 style={{ margin:0,fontSize:20,fontWeight:800 }}>{jig.name}</h2>
        </div>
        <div style={{ marginLeft:'auto',display:'flex',gap:8,alignItems:'center' }}>
          <StatusBadge status={jig.currentStatus} />
          <span style={{ fontSize:12,color:'var(--ni-text-muted)',fontFamily:'monospace' }}>Rev. {jig.currentRevision}</span>
        </div>
      </div>

      {error && <div style={{ color:'#EC2124',fontSize:13,margin:'12px 0',padding:'8px 12px',background:'#EC212411',borderRadius:6 }}>{error}</div>}

      <ProcedureRevisionBar entry={jig} requestedRevision={requestedRevision} revisions={availableRevisions} snapshot={snapshot}
        revisionLocked={editing || saving || jsonBusy || showVerifyModal || showMaintModal || verSaving || maintSaving}
        onRevisionChange={revision => { if (!(editing || saving || jsonBusy || showVerifyModal || showMaintModal || verSaving || maintSaving)) setSearchParams(revision ? { revision } : {}); }} allowAuthorDownload={false} />
      <ControlledDraftJson library="jigs" record={jig} revision={requestedRevision || snapshot?.revision || ''}
        blocked={editing || saving || showVerifyModal || showMaintModal || verSaving || maintSaving} onBusyChange={setJsonBusy} onApplied={load} />
      <ProcedureAuthoringMetadata value={editing ? form : jig} editing={editing} onChange={set} staff={canManage} />

      {/* Action bar */}
      {(canEditThis || availableActions.length > 0) && (
        <div style={{ display:'flex',gap:8,marginBottom:20,flexWrap:'wrap' }}>
          {!editing ? (
            <>
              {canEditThis && <button onClick={()=>setEditing(true)} disabled={!canEditThis || jsonBusy}
                style={{ background:'#a78bfa22',color:'#a78bfa',border:'1px solid #a78bfa44',borderRadius:6,
                  padding:'6px 16px',fontSize:12,fontWeight:700,cursor:'pointer' }}>Edit</button>}
              {availableActions.map(a=>(
                <button key={a.key} disabled={saving || jsonBusy || (a.key === 'approve' && unresolvedBlockers)} onClick={()=>{setActionModal(a.key);setActionNote('');}}
                  style={{ background:'rgba(255,255,255,0.05)',border:'1px solid var(--ni-border-soft)',
                    color:'var(--ni-text)',borderRadius:6,padding:'6px 16px',fontSize:12,fontWeight:600,cursor:'pointer' }}>
                  {a.label}
                </button>
              ))}
            </>
          ) : (
            <>
              <button onClick={()=>setShowSaveModal(true)}
                style={{ background:'#46BB89',color:'#000',border:'none',borderRadius:6,
                  padding:'6px 16px',fontSize:12,fontWeight:700,cursor:'pointer' }}>Save Changes</button>
              <button onClick={()=>{ setEditing(false); setForm(JSON.parse(JSON.stringify(jig))); }}
                style={{ background:'transparent',border:'1px solid var(--ni-border-soft)',color:'var(--ni-text)',
                  borderRadius:6,padding:'6px 16px',fontSize:12,cursor:'pointer' }}>Cancel</button>
            </>
          )}
        </div>
      )}

      {/* Content */}
      <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16 }}>
        <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16 }}>
          <div style={{ marginBottom:12 }}><div style={labelS}>Type</div>
            {editing ? <select value={form.type} onChange={e=>set('type',e.target.value)} style={inp}>
              {JIG_TYPES.map(t=><option key={t}>{t}</option>)}</select>
            : readVal(jig.type)}</div>
          <div style={{ marginBottom:12 }}><div style={labelS}>Manufacturer</div>
            {editing ? <input value={form.manufacturer||''} onChange={e=>set('manufacturer',e.target.value)} style={inp} />
            : readVal(jig.manufacturer)}</div>
          <div><div style={labelS}>Part Number</div>
            {editing ? <input value={form.partNumber||''} onChange={e=>set('partNumber',e.target.value)} style={inp} />
            : readVal(jig.partNumber)}</div>
        </div>

        <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16 }}>
          <div style={{ marginBottom:12 }}><div style={labelS}>Material</div>
            {editing ? <input value={form.material||''} onChange={e=>set('material',e.target.value)} style={inp} />
            : readVal(jig.material)}</div>
          <div><div style={labelS}>Dimensions (W × D × H)</div>
            {editing ? (
              <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr 60px',gap:6 }}>
                {['width','depth','height'].map(d=>(
                  <input key={d} type="number" placeholder={d} value={form.dimensions?.[d]||''}
                    onChange={e=>setDim(d,e.target.value)} style={inp} />
                ))}
                <select value={form.dimensions?.unit||'mm'} onChange={e=>setDim('unit',e.target.value)} style={inp}>
                  {['mm','cm','m','inch'].map(u=><option key={u}>{u}</option>)}
                </select>
              </div>
            ) : (
              <span style={{ fontSize:14 }}>
                {jig.dimensions?.width||jig.dimensions?.depth||jig.dimensions?.height
                  ? `${jig.dimensions.width||'?'} × ${jig.dimensions.depth||'?'} × ${jig.dimensions.height||'?'} ${jig.dimensions.unit||'mm'}`
                  : <span style={{ color:'var(--ni-text-muted)' }}>—</span>}
              </span>
            )}
          </div>
        </div>
      </div>

      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
        <div style={{ marginBottom:12 }}><div style={labelS}>Description</div>
          {editing ? <textarea value={form.description||''} onChange={e=>set('description',e.target.value)}
            rows={3} style={{ ...inp,resize:'vertical' }} />
          : <p style={{ margin:0,fontSize:14,lineHeight:1.6 }}>{jig.description||<span style={{ color:'var(--ni-text-muted)' }}>—</span>}</p>}</div>
        <div><div style={labelS}>Notes</div>
          {editing ? <textarea value={form.notes||''} onChange={e=>set('notes',e.target.value)}
            rows={2} style={{ ...inp,resize:'vertical' }} />
          : <p style={{ margin:0,fontSize:14,lineHeight:1.6 }}>{jig.notes||<span style={{ color:'var(--ni-text-muted)' }}>—</span>}</p>}</div>
      </div>

      {/* Asset & identity — documentNumber is the QMS record; these identify the
          physical fixture, its drawing and who is accountable for it. */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
        <div style={{ ...labelS, marginBottom:12 }}>Asset &amp; Identity</div>
        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12 }}>
          {[['assetId','Asset ID / Tag'],['serialNumber','Serial Number'],['location','Location'],
            ['drawingNumber','Drawing No.'],['drawingRevision','Drawing Rev.'],['ownerOrg','Owner Org'],
            ['responsiblePerson','Responsible Person']].map(([k,lbl])=>(
            <div key={k}><div style={labelS}>{lbl}</div>
              {editing ? <input value={form[k]||''} onChange={e=>set(k,e.target.value)} style={inp} /> : readVal(jig[k])}</div>
          ))}
          <div><div style={labelS}>In-Service Date</div>
            {editing ? <input aria-label="Operational in-service date" type="date" readOnly value={jig.inServiceDate?String(jig.inServiceDate).slice(0,10):''} style={inp} />
            : <span style={{ fontSize:14 }}>{jig.inServiceDate?new Date(jig.inServiceDate).toLocaleDateString():<span style={{ color:'var(--ni-text-muted)' }}>—</span>}</span>}</div>
          <div><div style={labelS}>Decommissioned</div>
            {editing ? <input aria-label="Operational decommissioned date" type="date" readOnly value={jig.decommissionedAt?String(jig.decommissionedAt).slice(0,10):''} style={inp} />
            : <span style={{ fontSize:14 }}>{jig.decommissionedAt?new Date(jig.decommissionedAt).toLocaleDateString():<span style={{ color:'var(--ni-text-muted)' }}>—</span>}</span>}</div>
        </div>
      </div>

      {/* Verification — the fixture analogue of equipment calibration */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8 }}>
          <div style={labelS}>Verification</div>
          {!editing && jig.verificationRequired && canLogEvents && (
            <button onClick={()=>setShowVerifyModal(true)}
              style={{ background:'#a78bfa22',color:'#a78bfa',border:'1px solid #a78bfa44',borderRadius:6,
                padding:'4px 12px',fontSize:12,fontWeight:700,cursor:'pointer' }}>
              + Log Verification
            </button>
          )}
        </div>
        {editing ? (
          <>
            <div style={{ display:'flex',alignItems:'center',gap:10,marginBottom:10 }}>
              <input type="checkbox" id="verreq" checked={!!form.verificationRequired}
                onChange={e=>set('verificationRequired',e.target.checked)} />
              <label htmlFor="verreq" style={{ fontSize:13,cursor:'pointer' }}>Verification Required</label>
            </div>
            {form.verificationRequired && (
              <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:12 }}>
                <div><div style={labelS}>Interval (days)</div>
                  <input type="number" value={form.verificationIntervalDays||''} onChange={e=>set('verificationIntervalDays',e.target.value)} style={inp} /></div>
                <div><div style={labelS}>Method</div>
                  <input value={form.verificationMethod||''} onChange={e=>set('verificationMethod',e.target.value)} style={inp} /></div>
                <div><div style={labelS}>Standard</div>
                  <input value={form.verificationStandard||''} onChange={e=>set('verificationStandard',e.target.value)} style={inp} /></div>
                <div><div style={labelS}>Status</div>
                  <span style={{ fontSize:13 }}>{jig.verificationStatus || 'Not Required'}</span></div>
              </div>
            )}
          </>
        ) : jig.verificationRequired ? (
          <>
            <div style={{ display:'flex',gap:16,flexWrap:'wrap',marginBottom:8 }}>
              <div style={{ fontSize:13,color:'var(--ni-text-muted)' }}>
                Every {jig.verificationIntervalDays||'?'} days{jig.verificationMethod?` · ${jig.verificationMethod}`:''}{jig.verificationStandard?` · ${jig.verificationStandard}`:''}
              </div>
              {verStatus && (
                <span style={{ fontSize:11,fontWeight:700,color:verStatusColor[verStatus],
                  background:verStatusColor[verStatus]+'22',borderRadius:4,padding:'2px 8px',letterSpacing:'0.04em' }}>
                  {verStatusLabel[verStatus]}
                </span>
              )}
              {(jig.verificationStatus==='Out of Service'||jig.verificationStatus==='Out of Verification') && (
                <span style={{ fontSize:11,fontWeight:700,color:'#EC2124',background:'#EC212422',borderRadius:4,padding:'2px 8px',letterSpacing:'0.04em' }}>
                  {jig.verificationStatus.toUpperCase()}
                </span>
              )}
            </div>
            <div style={{ display:'flex',gap:24,fontSize:12,color:'var(--ni-text-muted)' }}>
              <span>Last: {jig.lastVerifiedAt ? new Date(jig.lastVerifiedAt).toLocaleDateString() : '—'}</span>
              <span>Next due: {jig.nextVerificationDue ? new Date(jig.nextVerificationDue).toLocaleDateString() : '—'}</span>
            </div>
          </>
        ) : (
          <div style={{ fontSize:14,color:'var(--ni-text-muted)' }}>Not required</div>
        )}

        {!editing && jig.verificationRequired && (jig.verificationHistory||[]).length > 0 && (
          <div style={{ marginTop:14 }}>
            <div style={{ fontSize:11,fontWeight:700,letterSpacing:'0.06em',textTransform:'uppercase',
              color:'var(--ni-text-muted)',marginBottom:6 }}>History</div>
            <table style={{ width:'100%',borderCollapse:'collapse',fontSize:12 }}>
              <thead>
                <tr style={{ color:'var(--ni-text-muted)',fontSize:10,textTransform:'uppercase',letterSpacing:'0.06em' }}>
                  {['Date','By','Result','Certificate','Notes'].map(h=>(
                    <th key={h} style={{ padding:'4px 8px',textAlign:'left',fontWeight:600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...(jig.verificationHistory||[])].reverse().map((e,i)=>(
                  <tr key={i} style={{ borderTop:'1px solid var(--ni-border-soft)' }}>
                    <td style={{ padding:'7px 8px',whiteSpace:'nowrap' }}>{new Date(e.date).toLocaleDateString()}</td>
                    <td style={{ padding:'7px 8px',color:'var(--ni-text-muted)' }}>{e.performedByName||'—'}</td>
                    <td style={{ padding:'7px 8px' }}>
                      <span style={{ fontSize:10,fontWeight:700,color:e.passed?'#46BB89':'#EC2124',
                        background:e.passed?'#46BB8920':'#EC212420',borderRadius:3,padding:'1px 6px' }}>
                        {e.passed?'PASS':'FAIL'}
                      </span>
                      {e.outOfTolerance && <span style={{ marginLeft:6,fontSize:10,fontWeight:700,color:'#EC2124' }}>OOT</span>}
                    </td>
                    <td style={{ padding:'7px 8px',color:'var(--ni-text-muted)',fontFamily:'monospace',fontSize:11 }}>{e.certificate||'—'}</td>
                    <td style={{ padding:'7px 8px',color:'var(--ni-text-muted)' }}>{e.notes||'—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Maintenance / servicing */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8 }}>
          <div style={labelS}>Maintenance</div>
          {!editing && jig.maintenanceRequired && canLogEvents && (
            <button onClick={()=>setShowMaintModal(true)}
              style={{ background:'#a78bfa22',color:'#a78bfa',border:'1px solid #a78bfa44',borderRadius:6,
                padding:'4px 12px',fontSize:12,fontWeight:700,cursor:'pointer' }}>
              + Log Maintenance
            </button>
          )}
        </div>
        {editing ? (
          <>
            <div style={{ display:'flex',alignItems:'center',gap:10,marginBottom:10 }}>
              <input type="checkbox" id="maintreq" checked={!!form.maintenanceRequired}
                onChange={e=>set('maintenanceRequired',e.target.checked)} />
              <label htmlFor="maintreq" style={{ fontSize:13,cursor:'pointer' }}>Scheduled Maintenance Required</label>
            </div>
            {form.maintenanceRequired && (
              <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12 }}>
                <div><div style={labelS}>Interval (days)</div>
                  <input type="number" value={form.maintenanceIntervalDays||''} onChange={e=>set('maintenanceIntervalDays',e.target.value)} style={inp} /></div>
              </div>
            )}
          </>
        ) : jig.maintenanceRequired ? (
          <div style={{ display:'flex',gap:24,fontSize:12,color:'var(--ni-text-muted)' }}>
            <span>Every {jig.maintenanceIntervalDays||'?'} days</span>
            <span>Last: {jig.lastMaintainedAt ? new Date(jig.lastMaintainedAt).toLocaleDateString() : '—'}</span>
            <span>Next due: {jig.nextMaintenanceDue ? new Date(jig.nextMaintenanceDue).toLocaleDateString() : '—'}</span>
          </div>
        ) : (
          <div style={{ fontSize:14,color:'var(--ni-text-muted)' }}>Not required</div>
        )}

        {!editing && (jig.maintenanceHistory||[]).length > 0 && (
          <div style={{ marginTop:14 }}>
            <div style={{ fontSize:11,fontWeight:700,letterSpacing:'0.06em',textTransform:'uppercase',
              color:'var(--ni-text-muted)',marginBottom:6 }}>History</div>
            <table style={{ width:'100%',borderCollapse:'collapse',fontSize:12 }}>
              <thead>
                <tr style={{ color:'var(--ni-text-muted)',fontSize:10,textTransform:'uppercase',letterSpacing:'0.06em' }}>
                  {['Date','Type','By','Work done','Notes'].map(h=>(
                    <th key={h} style={{ padding:'4px 8px',textAlign:'left',fontWeight:600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...(jig.maintenanceHistory||[])].reverse().map((e,i)=>(
                  <tr key={i} style={{ borderTop:'1px solid var(--ni-border-soft)' }}>
                    <td style={{ padding:'7px 8px',whiteSpace:'nowrap' }}>{new Date(e.date).toLocaleDateString()}</td>
                    <td style={{ padding:'7px 8px',color:'var(--ni-text-muted)' }}>{e.type||'—'}</td>
                    <td style={{ padding:'7px 8px',color:'var(--ni-text-muted)' }}>{e.performedByName||'—'}</td>
                    <td style={{ padding:'7px 8px' }}>{e.workDone||'—'}</td>
                    <td style={{ padding:'7px 8px',color:'var(--ni-text-muted)' }}>{e.notes||'—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Measurement Systems Analysis (IATF 16949 §7.1.5.1.1) — gauging fixtures */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10 }}>
          <div style={labelS}>Measurement Systems Analysis (MSA)</div>
          {editing && (
            <label style={{ display:'flex',alignItems:'center',gap:6,fontSize:12,cursor:'pointer' }}>
              <input type="checkbox" checked={!!form.msaRequired} onChange={e=>set('msaRequired',e.target.checked)} /> MSA required
            </label>
          )}
        </div>
        {!editing && jig.msaRequired && <div style={{ fontSize:12,color:'#a78bfa',marginBottom:8 }}>MSA required for this gauging fixture</div>}
        {(jig.msaHistory||[]).length === 0 ? (
          <div style={{ fontSize:13,color:'var(--ni-text-muted)' }}>No MSA studies recorded.</div>
        ) : (jig.msaHistory||[]).map((m,i)=>(
          <div key={i} style={{ display:'flex',gap:10,alignItems:'center',padding:'6px 0',borderBottom:'1px solid var(--ni-border-soft)',fontSize:13,flexWrap:'wrap' }}>
            <span style={{ fontWeight:600 }}>{m.studyType}</span>
            {m.percentRR!=null && m.percentRR!=='' && <span style={{ color:'var(--ni-text-muted)' }}>%R&R: {m.percentRR}</span>}
            {m.result && <span style={{ color:'var(--ni-text-muted)' }}>{m.result}</span>}
            <span style={{ marginLeft:'auto',fontSize:10,fontWeight:700,color:m.acceptable?'#46BB89':'#EC2124',background:(m.acceptable?'#46BB89':'#EC2124')+'22',borderRadius:4,padding:'1px 6px' }}>{m.acceptable?'ACCEPTABLE':'NOT ACCEPTABLE'}</span>
          </div>
        ))}
        <p style={{ fontSize:12, color:'var(--ni-text-muted)' }}>MSA results are operational history and cannot be changed through a document edit.</p>
      </div>

      {/* Key metric — auto-fills when this jig is linked as a tool on a step */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
        <div style={labelS}>Key Metric</div>
        <div style={{ fontSize:12,color:'var(--ni-text-muted)',margin:'4px 0 10px' }}>The default spec auto-filled when this jig is linked as a tool on an assembly step.</div>
        {editing
          ? <MetricInput value={form.keyMetric||{}} onChange={v=>set('keyMetric',v)} getToken={getToken} accent="#a78bfa" />
          : (jig.keyMetric && jig.keyMetric.metricType
              ? <span style={{ fontSize:14 }}>{jig.keyMetric.metricType}: <strong>{jig.keyMetric.metricValue} {jig.keyMetric.metricUnit}</strong></span>
              : <span style={{ fontSize:14,color:'var(--ni-text-muted)' }}>—</span>)}
      </div>

      {/* Supplier visibility */}
      {canManage && (
        <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
          <div style={labelS}>Supplier Visibility</div>
          <div style={{ display:'flex',gap:8,flexWrap:'wrap',marginTop:4 }}>
            {SUPPLIERS.map(s=>(
              editing ? (
                <button type="button" key={s} onClick={()=>toggleSupplier(s)}
                  style={{ padding:'5px 14px',borderRadius:20,fontSize:12,fontWeight:700,cursor:'pointer',border:'1px solid',
                    background:(form.visibility?.suppliers||[]).includes(s)?'#a78bfa22':'transparent',
                    color:(form.visibility?.suppliers||[]).includes(s)?'#a78bfa':'var(--ni-text-muted)',
                    borderColor:(form.visibility?.suppliers||[]).includes(s)?'#a78bfa':'var(--ni-border-soft)' }}>
                  {s.toUpperCase()}
                </button>
              ) : (
                <span key={s} style={{ padding:'3px 12px',borderRadius:20,fontSize:11,fontWeight:700,
                  background:(jig.visibility?.suppliers||[]).includes(s)?'#a78bfa22':'rgba(255,255,255,0.04)',
                  color:(jig.visibility?.suppliers||[]).includes(s)?'#a78bfa':'var(--ni-text-muted)' }}>
                  {s.toUpperCase()}
                </span>
              )
            ))}
          </div>
        </div>
      )}

      {/* Applicable Products */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
        <div style={labelS}>Applicable Products</div>
        <div style={{ display:'flex',gap:6,flexWrap:'wrap',marginBottom:editing?8:0,marginTop:4 }}>
          {(editing?form:jig).applicableProducts?.map(t=>(
            <span key={t} style={{ padding:'3px 10px',borderRadius:20,fontSize:11,fontWeight:700,
              background:'#a78bfa22',color:'#a78bfa',display:'flex',alignItems:'center',gap:4 }}>
              {t}
              {editing && <button onClick={()=>removeTag(t)} style={{ background:'none',border:'none',color:'inherit',cursor:'pointer',padding:0,fontSize:12,lineHeight:1 }}>×</button>}
            </span>
          ))}
          {!editing&&(!jig.applicableProducts?.length)&&<span style={{ fontSize:12,color:'var(--ni-text-muted)' }}>—</span>}
        </div>
        {editing && (
          <div style={{ display:'flex',gap:8 }}>
            <input value={tagInput} onChange={e=>setTagInput(e.target.value)}
              onKeyDown={e=>{ if(e.key==='Enter'){e.preventDefault();addTag(tagInput);}}}
              placeholder="Type product name, press Enter"
              style={{ ...inp,flex:1 }} />
            <button type="button" onClick={()=>addTag(tagInput)}
              style={{ background:'#a78bfa22',color:'#a78bfa',border:'1px solid #a78bfa44',borderRadius:6,padding:'6px 12px',fontSize:12,cursor:'pointer' }}>Add</button>
          </div>
        )}
      </div>

      {/* Linked Equipment */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
        <div style={labelS}>Linked Equipment</div>
        <ResourcePicker
          endpoint="/quality/equipment" responseKey="items"
          items={editing ? form.linkedEquipment||[] : jig.linkedEquipment||[]}
          onChange={v=>set('linkedEquipment',v)}
          getToken={getToken} color="#f59e0b" label="equipment"
          disabled={!editing} detailPath="/quality/equipment"
        />
      </div>

      {/* Used In Flows */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
        <div style={labelS}>Used In Process Flows</div>
        {usedInFlows.length===0
          ? <div style={{ fontSize:13,color:'var(--ni-text-muted)',marginTop:4 }}>Not linked to any flow yet.</div>
          : usedInFlows.map(f=>(
            <div key={f._id} onClick={()=>navigate(`/quality/process/${f._id}`)}
              style={{ display:'flex',gap:10,alignItems:'center',padding:'7px 0',
                borderBottom:'1px solid var(--ni-border-soft)',cursor:'pointer' }}
              onMouseEnter={e=>e.currentTarget.style.opacity='0.75'}
              onMouseLeave={e=>e.currentTarget.style.opacity='1'}>
              <span style={{ fontFamily:'monospace',fontSize:11,color:'#60a5fa',flexShrink:0 }}>{f.documentNumber}</span>
              <span style={{ fontSize:13,flex:1 }}>{f.name}</span>
              <StatusBadge status={f.currentStatus}/>
            </div>
          ))
        }
      </div>

      {/* Source of record */}
      {jig.source?.url && (
        <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
          <div style={labelS}>Source of record</div>
          <a href={jig.source.url} target="_blank" rel="noopener noreferrer"
            style={{ fontSize:13,color:'#a78bfa',textDecoration:'none',wordBreak:'break-word' }}>
            {(jig.source.system||'SharePoint')}{jig.source.fileName?` — ${jig.source.fileName}`:''} ↗
          </a>
          {jig.source.externalModifiedAt && <div style={{ fontSize:11,color:'var(--ni-text-muted)',marginTop:4 }}>Updated {new Date(jig.source.externalModifiedAt).toLocaleDateString()}</div>}
        </div>
      )}

      {/* Revision history */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16 }}>
        <div style={labelS}>Revision History</div>
        <table style={{ width:'100%',borderCollapse:'collapse',fontSize:12,marginTop:8 }}>
          <thead>
            <tr style={{ color:'var(--ni-text-muted)',fontSize:10,textTransform:'uppercase',letterSpacing:'0.06em' }}>
              {['Rev','Status','Summary','By','Date'].map(h=>(
                <th key={h} style={{ padding:'6px 10px',textAlign:'left',fontWeight:600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...( jig.revisions||[])].reverse().map((r,i)=>(
              <tr key={i} style={{ borderTop:'1px solid var(--ni-border-soft)' }}>
                <td style={{ padding:'8px 10px',fontFamily:'monospace' }}>{r.revision}</td>
                <td style={{ padding:'8px 10px' }}><StatusBadge status={r.status}/></td>
                <td style={{ padding:'8px 10px',color:'var(--ni-text-muted)' }}>{r.changeSummary}</td>
                <td style={{ padding:'8px 10px',color:'var(--ni-text-muted)' }}>{r.updatedByName}</td>
                <td style={{ padding:'8px 10px',color:'var(--ni-text-muted)',whiteSpace:'nowrap' }}>
                  {r.updatedAt?new Date(r.updatedAt).toLocaleDateString():'—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Save modal */}
      {showSaveModal && (
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000 }}>
          <div style={{ background:'var(--ni-bg)',border:'1px solid var(--ni-border-soft)',borderRadius:12,padding:28,width:420 }}>
            <h3 style={{ margin:'0 0 16px',fontSize:16 }}>Save Changes</h3>
            <div style={labelS}>Change Summary</div>
            <input value={changeSummary} onChange={e=>setChangeSummary(e.target.value)}
              placeholder="Describe what changed…" style={{ ...inp,marginBottom:20 }} />
            <div style={{ display:'flex',gap:10 }}>
              <button onClick={handleSave} disabled={saving}
                style={{ background:'#46BB89',color:'#000',border:'none',borderRadius:6,padding:'9px 20px',fontWeight:700,fontSize:13,cursor:'pointer' }}>
                {saving?'Saving…':'Save'}
              </button>
              <button onClick={()=>setShowSaveModal(false)}
                style={{ background:'transparent',border:'1px solid var(--ni-border-soft)',color:'var(--ni-text)',borderRadius:6,padding:'9px 16px',fontSize:13,cursor:'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Action modal */}
      {actionModal && (
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000 }}>
          <div style={{ background:'var(--ni-bg)',border:'1px solid var(--ni-border-soft)',borderRadius:12,padding:28,width:420 }}>
            <h3 style={{ margin:'0 0 16px',fontSize:16 }}>{TRANSITIONS[actionModal]?.label}</h3>
            <div style={labelS}>Note (optional)</div>
            <input value={actionNote} onChange={e=>setActionNote(e.target.value)}
              placeholder="Reason or comment…" style={{ ...inp,marginBottom:20 }} />
            <div style={{ display:'flex',gap:10 }}>
              <button onClick={()=>handleAction(actionModal)} disabled={saving}
                style={{ background:'#46BB89',color:'#000',border:'none',borderRadius:6,padding:'9px 20px',fontWeight:700,fontSize:13,cursor:'pointer' }}>
                {saving?'Processing…':'Confirm'}
              </button>
              <button onClick={()=>setActionModal(null)}
                style={{ background:'transparent',border:'1px solid var(--ni-border-soft)',color:'var(--ni-text)',borderRadius:6,padding:'9px 16px',fontSize:13,cursor:'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Verification log modal */}
      {showVerifyModal && (
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000 }}>
          <div style={{ background:'var(--ni-bg)',border:'1px solid var(--ni-border-soft)',borderRadius:12,padding:28,width:440,maxHeight:'90vh',overflow:'auto' }}>
            <h3 style={{ margin:'0 0 16px',fontSize:16 }}>Log Verification</h3>
            <div style={{ display:'flex',flexDirection:'column',gap:14 }}>
              <div><div style={labelS}>Date *</div>
                <input type="date" value={verForm.date} onChange={e=>setVerForm(f=>({...f,date:e.target.value}))} style={inp} /></div>
              <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10 }}>
                <div><div style={labelS}>Method</div>
                  <input value={verForm.method} onChange={e=>setVerForm(f=>({...f,method:e.target.value}))} style={inp} placeholder="e.g. CMM, go/no-go" /></div>
                <div><div style={labelS}>Certificate / Reference</div>
                  <input value={verForm.certificate} onChange={e=>setVerForm(f=>({...f,certificate:e.target.value}))} style={inp} placeholder="e.g. VER-2026-0042" /></div>
              </div>
              {/* Metrological traceability */}
              <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10 }}>
                <div><div style={labelS}>Verified by (org)</div>
                  <input value={verForm.verifiedByOrg} onChange={e=>setVerForm(f=>({...f,verifiedByOrg:e.target.value}))} style={inp} placeholder="e.g. NI metrology" /></div>
                <div><div style={labelS}>Traceable to</div>
                  <input value={verForm.traceableTo} onChange={e=>setVerForm(f=>({...f,traceableTo:e.target.value}))} style={inp} placeholder="e.g. NIST / national std" /></div>
              </div>
              <div><div style={labelS}>Reference standard / gauge used</div>
                <input value={verForm.referenceStandardUsed} onChange={e=>setVerForm(f=>({...f,referenceStandardUsed:e.target.value}))} style={inp} placeholder="e.g. Cal master block" /></div>
              <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10 }}>
                <div><div style={labelS}>As found</div>
                  <input value={verForm.asFound} onChange={e=>setVerForm(f=>({...f,asFound:e.target.value}))} style={inp} placeholder="reading before adj." /></div>
                <div><div style={labelS}>As left</div>
                  <input value={verForm.asLeft} onChange={e=>setVerForm(f=>({...f,asLeft:e.target.value}))} style={inp} placeholder="reading after adj." /></div>
              </div>
              <div><div style={labelS}>Notes</div>
                <textarea value={verForm.notes} onChange={e=>setVerForm(f=>({...f,notes:e.target.value}))}
                  rows={2} style={{ ...inp,resize:'vertical' }} placeholder="Observations, conditions…" /></div>
              <div style={{ display:'flex',gap:18,flexWrap:'wrap' }}>
                <label style={{ display:'flex',alignItems:'center',gap:8,fontSize:13,cursor:'pointer' }}>
                  <input type="checkbox" checked={verForm.passed} onChange={e=>setVerForm(f=>({...f,passed:e.target.checked}))} style={{ accentColor:'#46BB89' }} />
                  Verification passed
                </label>
                <label style={{ display:'flex',alignItems:'center',gap:8,fontSize:13,cursor:'pointer' }}>
                  <input type="checkbox" checked={verForm.outOfTolerance} onChange={e=>setVerForm(f=>({...f,outOfTolerance:e.target.checked}))} style={{ accentColor:'#EC2124' }} />
                  Found out of tolerance
                </label>
              </div>
              {verForm.outOfTolerance && (
                <>
                  <div><div style={labelS}>Impact assessment (prior product)</div>
                    <textarea value={verForm.impactAssessment} onChange={e=>setVerForm(f=>({...f,impactAssessment:e.target.value}))}
                      rows={2} style={{ ...inp,resize:'vertical' }} placeholder="Risk to product built on this fixture since last good check…" /></div>
                  <label style={{ display:'flex',alignItems:'center',gap:8,fontSize:13,cursor:'pointer' }}>
                    <input type="checkbox" checked={verForm.customerNotified} onChange={e=>setVerForm(f=>({...f,customerNotified:e.target.checked}))} />
                    Customer notified
                  </label>
                </>
              )}
              <div style={{ display:'flex',gap:10 }}>
                <button onClick={logVerification} disabled={verSaving||!verForm.date}
                  style={{ background:'#a78bfa',color:'#000',border:'none',borderRadius:6,padding:'9px 20px',fontWeight:700,fontSize:13,cursor:(verSaving||!verForm.date)?'not-allowed':'pointer',opacity:(verSaving||!verForm.date)?0.6:1 }}>
                  {verSaving?'Saving…':'Save Log'}
                </button>
                <button onClick={()=>setShowVerifyModal(false)}
                  style={{ background:'transparent',border:'1px solid var(--ni-border-soft)',color:'var(--ni-text)',borderRadius:6,padding:'9px 16px',fontSize:13,cursor:'pointer' }}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Maintenance log modal */}
      {showMaintModal && (
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000 }}>
          <div style={{ background:'var(--ni-bg)',border:'1px solid var(--ni-border-soft)',borderRadius:12,padding:28,width:420 }}>
            <h3 style={{ margin:'0 0 16px',fontSize:16 }}>Log Maintenance</h3>
            <div style={{ display:'flex',flexDirection:'column',gap:14 }}>
              <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10 }}>
                <div><div style={labelS}>Date *</div>
                  <input type="date" value={maintForm.date} onChange={e=>setMaintForm(f=>({...f,date:e.target.value}))} style={inp} /></div>
                <div><div style={labelS}>Type</div>
                  <select value={maintForm.type} onChange={e=>setMaintForm(f=>({...f,type:e.target.value}))} style={inp}>
                    {['Preventive','Corrective','Inspection','Repair','Other'].map(t=><option key={t}>{t}</option>)}
                  </select></div>
              </div>
              <div><div style={labelS}>Work done</div>
                <input value={maintForm.workDone} onChange={e=>setMaintForm(f=>({...f,workDone:e.target.value}))} style={inp} placeholder="e.g. Replaced locating pins" /></div>
              <div><div style={labelS}>Notes</div>
                <textarea value={maintForm.notes} onChange={e=>setMaintForm(f=>({...f,notes:e.target.value}))}
                  rows={2} style={{ ...inp,resize:'vertical' }} /></div>
              <div style={{ display:'flex',gap:10 }}>
                <button onClick={logMaintenance} disabled={maintSaving||!maintForm.date}
                  style={{ background:'#a78bfa',color:'#000',border:'none',borderRadius:6,padding:'9px 20px',fontWeight:700,fontSize:13,cursor:(maintSaving||!maintForm.date)?'not-allowed':'pointer',opacity:(maintSaving||!maintForm.date)?0.6:1 }}>
                  {maintSaving?'Saving…':'Save Log'}
                </button>
                <button onClick={()=>setShowMaintModal(false)}
                  style={{ background:'transparent',border:'1px solid var(--ni-border-soft)',color:'var(--ni-text)',borderRadius:6,padding:'9px 16px',fontSize:13,cursor:'pointer' }}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
